import { useCallback, useMemo, useState } from "react";
import { useMistralNarrativeOverlay } from "@/hooks/useMistralNarrativeOverlay";
import { bulletsToActionQueue } from "@/utils/mistralInsightMappers";
import {
  AlertTriangle,
  Search,
  TrendingUp,
  Award,
  Users,
  ClipboardList,
} from "lucide-react";
import { SchoolLink, BlockLink } from "@/components/DrilldownLink";
import {
  buildSmiGaugeStyle,
  buildSchoolMovementIndex,
  buildDrilldownHeroHeadline,
  buildDrilldownHeroNarrative,
  buildDrilldownStatusChips,
  buildDrilldownPrimaryAction,
  buildDrilldownKpis,
  buildRankingList,
  buildSpotlightCards,
  buildBlockMetricsTable,
  buildPriorityIndicators,
  buildDrilldownActionQueue,
  buildDrilldownReviewNote,
  filterSchools,
  uniqueBlocks,
  buildTeacherDrilldownMarkdown,
} from "@/utils/teacherDrilldownDerived";
import { toTitleCase } from "../utils/toTitleCase";

const SPOTLIGHT_ICONS = {
  green: TrendingUp,
  red: AlertTriangle,
  "": Award,
  purple: Users,
};

const ACTION_ICONS = {
  red: AlertTriangle,
  green: TrendingUp,
  orange: ClipboardList,
};

const scrollToId = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const TeacherDrilldownTab = ({
  overview = {},
  topGains = [],
  topLosses = [],
  blockData = [],
  schoolDistribution = [],
}) => {
  const [search, setSearch] = useState("");
  const [blockFilter, setBlockFilter] = useState("All");
  const [movementFilter, setMovementFilter] = useState("Gain/Loss");
  const [priorityFilter, setPriorityFilter] = useState("All");
  const [applied, setApplied] = useState({ search: "", block: "All", movement: "Gain/Loss", priority: "All" });

  const smi = useMemo(() => buildSchoolMovementIndex(schoolDistribution), [schoolDistribution]);
  const gaugeStyle = useMemo(() => buildSmiGaugeStyle(smi), [smi]);
  const hasData = Boolean(topLosses?.length || topGains?.length || blockData?.length);

  const buildLocalMarkdown = useCallback(
    () => buildTeacherDrilldownMarkdown(topLosses, topGains),
    [topLosses, topGains]
  );
  const scopeKey = useMemo(
    () => JSON.stringify({ l: topLosses?.length, g: topGains?.length }),
    [topLosses?.length, topGains?.length]
  );
  const {
    loading: aiLoading,
    pickHeadline,
    pickFromBucket,
    pickPrimaryAction,
    pickMapped,
    isMistral,
  } = useMistralNarrativeOverlay({
    kind: "teacher-drilldown",
    title: "Teacher School Drilldown",
    buildLocalMarkdown,
    context: { overview, topLosses, topGains, blockData },
    enabled: hasData,
    scopeKey,
  });

  const localHeadline = useMemo(() => buildDrilldownHeroHeadline(), []);
  const localNarrative = useMemo(() => buildDrilldownHeroNarrative(topLosses), [topLosses]);
  const headline = useMemo(
    () => (aiLoading ? localHeadline : pickHeadline(localHeadline)),
    [aiLoading, pickHeadline, localHeadline]
  );
  const narrative = useMemo(
    () => (aiLoading ? localNarrative : pickFromBucket(localNarrative, "insights", 1)),
    [aiLoading, pickFromBucket, localNarrative]
  );
  const statusChips = useMemo(() => {
    const chips = buildDrilldownStatusChips(topGains, topLosses, blockData);
    if (isMistral && !aiLoading) {
      return [{ label: "Mistral AI Assisted", className: "purple" }, ...chips];
    }
    return chips;
  }, [topGains, topLosses, blockData, isMistral, aiLoading]);
  const localPrimaryAction = useMemo(() => buildDrilldownPrimaryAction(topLosses), [topLosses]);
  const primaryAction = useMemo(
    () => (aiLoading ? localPrimaryAction : pickPrimaryAction(localPrimaryAction)),
    [aiLoading, pickPrimaryAction, localPrimaryAction]
  );
  const kpis = useMemo(() => buildDrilldownKpis(overview, topLosses), [overview, topLosses]);
  const spotlightCards = useMemo(() => buildSpotlightCards(blockData), [blockData]);
  const blockRows = useMemo(() => buildBlockMetricsTable(blockData), [blockData]);
  const priorityRows = useMemo(
    () => buildPriorityIndicators(topLosses, topGains, blockData),
    [topLosses, topGains, blockData]
  );
  const localActionQueue = useMemo(() => buildDrilldownActionQueue(topLosses), [topLosses]);
  const actionQueue = useMemo(
    () => pickMapped(localActionQueue, "recs", bulletsToActionQueue),
    [pickMapped, localActionQueue]
  );
  const localReviewNote = useMemo(
    () => buildDrilldownReviewNote(topLosses, topGains),
    [topLosses, topGains]
  );
  const reviewNote = useMemo(
    () => (aiLoading ? localReviewNote : pickFromBucket(localReviewNote, "actions", 0)),
    [aiLoading, pickFromBucket, localReviewNote]
  );
  const blockOptions = useMemo(() => uniqueBlocks(blockData), [blockData]);

  const filteredGains = useMemo(() => {
    let rows = filterSchools(topGains, applied);
    if (applied.priority === "Critical") rows = rows.filter((s) => (s.change ?? 0) > 40);
    return buildRankingList(rows, "gain");
  }, [topGains, applied]);

  const filteredLosses = useMemo(() => {
    let rows = filterSchools(topLosses, applied);
    if (applied.priority === "Critical") rows = rows.filter((s) => (s.change ?? 0) < -20);
    return buildRankingList(rows, "loss");
  }, [topLosses, applied]);

  const handleApply = () => {
    setApplied({ search, block: blockFilter, movement: movementFilter, priority: priorityFilter });
  };

  return (
    <div className="tdr-drill-tab" data-testid="teacher-drilldown">
      <section className="tdr-hero-card" id="tdr-hero">
        <div className="tdr-hero-layout">
          <div>
            <div className="tdr-school-main">
              <div
                className="tdr-school-gauge"
                style={gaugeStyle}
                aria-label={smi == null ? "School movement index unavailable" : `School movement index ${smi} percent`}
              >
                <div>
                  <strong>{smi == null ? "—" : `${smi}%`}</strong>
                  <span>SMI</span>
                </div>
              </div>
              <div className="tdr-hero-copy">
                <h2>{toTitleCase(headline)}</h2>
                <p>{narrative}</p>
                <div className="tdr-status-row">
                  {statusChips.map((chip) => (
                    <span key={chip.label} className={`tdr-status-chip ${chip.className}`.trim()}>
                      {chip.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="tdr-summary-note">
              <div className="tdr-icon-box orange">
                <AlertTriangle size={22} />
              </div>
              <div>
                <b>Primary school-level action</b>
                <span>{primaryAction}</span>
              </div>
            </div>
          </div>

          <div className="tdr-metric-grid" aria-label="School drilldown KPIs">
            {kpis.map((card) => (
              <article key={card.label} className={`tdr-metric-card ${card.cardClass}`.trim()}>
                <small>{card.label}</small>
                <strong>{card.value}</strong>
                <span>{card.sub}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="tdr-filter-panel" aria-label="School drilldown filters">
        <label className="tdr-filter-field">
          <Search size={18} />
          <input
            aria-label="Search school"
            placeholder="Search school name or UDISE code"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <label className="tdr-filter-field">
          Block <b>{blockFilter}</b>
          <select
            className="tdr-filter-select"
            value={blockFilter}
            onChange={(e) => setBlockFilter(e.target.value)}
            aria-label="Filter by block"
          >
            {blockOptions.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </label>
        <label className="tdr-filter-field">
          Movement <b>{movementFilter}</b>
          <select
            className="tdr-filter-select"
            value={movementFilter}
            onChange={(e) => setMovementFilter(e.target.value)}
            aria-label="Filter by movement"
          >
            <option value="Gain/Loss">Gain/Loss</option>
            <option value="Gain">Gain</option>
            <option value="Loss">Loss</option>
          </select>
        </label>
        <label className="tdr-filter-field">
          Priority <b>{priorityFilter}</b>
          <select
            className="tdr-filter-select"
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            aria-label="Filter by priority"
          >
            <option value="All">All</option>
            <option value="Critical">Critical</option>
          </select>
        </label>
        <button type="button" className="tdr-filter-go" onClick={handleApply}>
          Apply
        </button>
      </section>

      <div className="tdr-section-title" id="tdr-schools">
        <div>
          <h2>Schools Requiring Attention</h2>
          <p>
            Gain and loss lists are shown side by side with cleaner ranking cards, making it easier
            to separate expansion signals from staffing-risk signals.
          </p>
        </div>
        <button type="button" className="tdr-section-action" onClick={() => scrollToId("tdr-details")}>
          View detailed matrix →
        </button>
      </div>

      <section className="tdr-two-column">
        <article className="tdr-panel">
          <div className="tdr-panel-head">
            <div>
              <h3>Top 10 Schools · Highest Staffing Gain</h3>
              <p>Schools with the largest positive CY movement versus PY.</p>
            </div>
            <span className="tdr-pill green">Growth</span>
          </div>
          <div className="tdr-ranking-list">
            {filteredGains.map((row) => (
              <div key={`${row.udiseCode}-${row.rank}`} className="tdr-rank-item">
                <div className="tdr-rank-no">{row.rank}</div>
                <div className="tdr-rank-school">
                  <b>
                    {row.udiseCode ? (
                      <SchoolLink udiseCode={row.udiseCode}>{row.schoolName}</SchoolLink>
                    ) : (
                      row.schoolName
                    )}
                  </b>
                  <span>
                    {row.blockName} · PY {row.teachersPy} · CY {row.teachersCy}
                  </span>
                </div>
                <div className="tdr-mini-kpi">{row.teachersCy} CY</div>
                <span className="tdr-change-badge">{row.changeLabel}</span>
              </div>
            ))}
          </div>
        </article>

        <article className="tdr-panel">
          <div className="tdr-panel-head">
            <div>
              <h3>Top 10 Schools · Highest Staffing Loss</h3>
              <p>Schools with sharp declines that should be checked before next review.</p>
            </div>
            <span className="tdr-pill red">Risk</span>
          </div>
          <div className="tdr-ranking-list">
            {filteredLosses.map((row) => (
              <div key={`${row.udiseCode}-${row.rank}`} className="tdr-rank-item">
                <div className="tdr-rank-no">{row.rank}</div>
                <div className="tdr-rank-school">
                  <b>
                    {row.udiseCode ? (
                      <SchoolLink udiseCode={row.udiseCode}>{row.schoolName}</SchoolLink>
                    ) : (
                      row.schoolName
                    )}
                  </b>
                  <span>
                    {row.blockName} · PY {row.teachersPy} · CY {row.teachersCy}
                  </span>
                </div>
                <div className="tdr-mini-kpi">{row.teachersCy} CY</div>
                <span className={`tdr-change-badge ${row.badgeClass}`.trim()}>{row.changeLabel}</span>
              </div>
            ))}
          </div>
        </article>
      </section>

      <div className="tdr-section-title">
        <div>
          <h2>Block-Level School Drilldown Snapshot</h2>
          <p>
            Four summary cards translate the detailed table into decision-ready signals for officers
            and review committees.
          </p>
        </div>
        <button type="button" className="tdr-section-action" onClick={() => scrollToId("tdr-actions")}>
          Recommended actions →
        </button>
      </div>

      <section className="tdr-spotlight-grid">
        {spotlightCards.map((card) => {
          const Icon = SPOTLIGHT_ICONS[card.iconClass] ?? Award;
          return (
            <article key={card.id} className="tdr-spot-card">
              <div className={`tdr-icon-box ${card.iconClass}`.trim()}>
                <Icon size={22} />
              </div>
              <h3>{toTitleCase(card.title)}</h3>
              <div className="tdr-spot-value">
                <strong>{card.block}</strong>
                <span>{card.metric}</span>
              </div>
              <p>{card.description}</p>
            </article>
          );
        })}
      </section>

      <section className="tdr-wide-grid" id="tdr-details">
        <article className="tdr-panel tdr-table-card">
          <div className="tdr-table-head">
            <div>
              <h3>Block-Wise Detailed Metrics</h3>
              <p>Schools, current teachers, growth, quality and risk indicators in one place.</p>
            </div>
            <span className="tdr-pill">{blockRows.length} blocks</span>
          </div>
          <table className="tdr-metrics-table" aria-label="Block-wise detailed teacher metrics">
            <thead>
              <tr>
                <th>Block</th>
                <th className="numeric">Schools</th>
                <th className="numeric">Teachers CY</th>
                <th className="numeric">Growth</th>
                <th className="numeric">CTET</th>
                <th className="numeric">CWSN</th>
                <th className="numeric">Digital</th>
                <th className="numeric">TQI</th>
                <th className="numeric">TRI</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {blockRows.map((row) => (
                <tr key={row.blockName}>
                  <td>
                    {row.blockCode ? (
                      <BlockLink blockCode={row.blockCode} className="tdr-block-link">
                        {row.blockName}
                      </BlockLink>
                    ) : (
                      <span className="tdr-block-link">{row.blockName}</span>
                    )}
                  </td>
                  <td className="numeric">{row.schools.toLocaleString("en-IN")}</td>
                  <td className="numeric">{row.teachersCy.toLocaleString("en-IN")}</td>
                  <td className="numeric">
                    <span className={`tdr-tag ${row.growthClass}`.trim()}>{row.growthLabel}</span>
                  </td>
                  <td className="numeric">{row.ctet}</td>
                  <td className="numeric">{row.cwsn}</td>
                  <td className="numeric">{row.digital}</td>
                  <td className="numeric">
                    <span className="tdr-tag">{row.tqi}</span>
                  </td>
                  <td className="numeric">
                    <span className={`tdr-tag ${row.triClass}`.trim()}>{row.tri}</span>
                  </td>
                  <td>
                    <span className={`tdr-tag ${row.statusClass}`.trim()}>{row.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>

        <article className="tdr-panel" id="tdr-actions">
          <div className="tdr-panel-head">
            <div>
              <h3>School Action Cockpit</h3>
              <p>Focused next steps for school-level verification and block review.</p>
            </div>
            <span className="tdr-pill orange">Priority</span>
          </div>
          <div className="tdr-quality-strip" aria-label="Priority indicators">
            {priorityRows.map((row) => (
              <div key={row.label} className="tdr-quality-row">
                <b>{row.label}</b>
                <div className="tdr-track">
                  <i className={row.barClass} style={{ width: `${row.width}%` }} />
                </div>
                <span>{row.level}</span>
              </div>
            ))}
          </div>
          <div className="tdr-action-card">
            {actionQueue.map((item) => {
              const Icon = ACTION_ICONS[item.iconClass] ?? ClipboardList;
              return (
                <div key={item.title} className="tdr-action-item">
                  <div className={`tdr-icon-box ${item.iconClass}`.trim()}>
                    <Icon size={22} />
                  </div>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.body}</p>
                  </div>
                </div>
              );
            })}
            <div className="tdr-action-footer">
              <b>Next review recommendation</b>
              <span>{reviewNote}</span>
            </div>
          </div>
        </article>
      </section>
    </div>
  );
};

export default TeacherDrilldownTab;
